import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Divider, Tooltip, Paper } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
} from '@mui/icons-material';
import './RichTextEditor.css';

/**
 * Custom Rich Text Editor Component
 * Supports: Bold, Italic, Underline, Bullet Lists, Numbered Lists, Links
 */
const RichTextEditor = ({ value = '', onChange, placeholder = 'Start typing...', minHeight = '200px' }) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const handleBold = () => execCommand('bold');
  const handleItalic = () => execCommand('italic');
  const handleUnderline = () => execCommand('underline');
  const handleBulletList = () => execCommand('insertUnorderedList');
  const handleNumberedList = () => execCommand('insertOrderedList');

  const handleLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      // Ensure URL has protocol
      const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
      execCommand('createLink', formattedUrl);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleInput();
  };

  return (
    <Paper elevation={1} sx={{ border: isFocused ? '2px solid #1976d2' : '1px solid #e0e0e0', borderRadius: 1 }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          p: 1,
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          flexWrap: 'wrap',
        }}
      >
        <Tooltip title="Bold (Ctrl+B)">
          <IconButton size="small" onClick={handleBold} sx={{ '&:hover': { bgcolor: '#e0e0e0' } }}>
            <FormatBold fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Italic (Ctrl+I)">
          <IconButton size="small" onClick={handleItalic} sx={{ '&:hover': { bgcolor: '#e0e0e0' } }}>
            <FormatItalic fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Underline (Ctrl+U)">
          <IconButton size="small" onClick={handleUnderline} sx={{ '&:hover': { bgcolor: '#e0e0e0' } }}>
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Bullet List">
          <IconButton size="small" onClick={handleBulletList} sx={{ '&:hover': { bgcolor: '#e0e0e0' } }}>
            <FormatListBulleted fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Numbered List">
          <IconButton size="small" onClick={handleNumberedList} sx={{ '&:hover': { bgcolor: '#e0e0e0' } }}>
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Insert Link">
          <IconButton size="small" onClick={handleLink} sx={{ '&:hover': { bgcolor: '#e0e0e0' } }}>
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Editor Content */}
      <Box
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onPaste={handlePaste}
        className="rich-text-editor-content"
        sx={{
          minHeight,
          p: 2,
          outline: 'none',
          fontSize: '14px',
          lineHeight: 1.6,
          color: '#333',
          '&:empty:before': {
            content: `"${placeholder}"`,
            color: '#999',
          },
        }}
      />
    </Paper>
  );
};

export default RichTextEditor;
